import * as React from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Theme } from "@mui/material/styles";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import SidebarIcons from "app/icons/SidebarIcons";
import { Styles } from "../../styles";

export type DrawerMenuItem = {
  name: string;
  icon: string;
  route: string;
  to?: string;
  isNested?: boolean;
  IconComponent?: React.ComponentType<{ color?: string; width?: number; height?: number }>;
};

export type SystemTablesSubItem = {
  name: string;
  route: string;
  showDividerBefore?: boolean;
  showDividerAfter?: boolean;
};

type DrawerMenuProps = {
  styles: ReturnType<typeof Styles>;
  theme: Theme;
  menuItems: DrawerMenuItem[];
  systemTablesSubItems: SystemTablesSubItem[];
  isSystemManagementOpen: boolean;
  isSystemTablesExpanded: boolean;
  setIsSystemTablesExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  urlParam: string;
  isItemActive: (route: string) => boolean;
  isSubItemActive: (route: string) => boolean;
  openSystemManagement: () => void;
  setIsSystemManagementClosedManually: React.Dispatch<React.SetStateAction<boolean>>;
};

export const DrawerMenu = ({
  styles,
  theme,
  menuItems,
  systemTablesSubItems,
  isSystemManagementOpen,
  isSystemTablesExpanded,
  setIsSystemTablesExpanded,
  urlParam,
  isItemActive,
  isSubItemActive,
  openSystemManagement,
  setIsSystemManagementClosedManually,
}: DrawerMenuProps) => {
  const { t } = useTranslation();

  return (
    <>
      {menuItems.map((item) => {
        const isSystemMgmt = item.route === "system-management";
        const isSystemTables = item.route === "system-tables";
        const isActive = isSystemManagementOpen
          ? isItemActive(item.route)
          : isSystemMgmt
            ? isSystemManagementOpen
            : urlParam === item.route;
        const itemClass = isActive
          ? `${styles.drawerItem} ${styles.drawerListItems}`
          : styles.drawerItem;
        const textClass = isActive
          ? `${styles.drawerText} ${styles.drawerListItemText}`
          : styles.drawerText;
        const iconColor = isActive
          ? theme.palette.primary.main
          : theme.palette.text.primary;

        if (isSystemTables) {
          const isSectionActive = systemTablesSubItems.some((subItem) =>
            isSubItemActive(subItem.route)
          );
          const sectionColor = isSectionActive
            ? theme.palette.secondary.main
            : theme.palette.text.primary;

          return (
            <React.Fragment key={item.route}>
              <Box className={styles.drawerLink}>
                <ListItemButton
                  className={styles.drawerItem}
                  onClick={() => setIsSystemTablesExpanded((prev) => !prev)}
                >
                  <Box className={styles.nestedItemContent}>
                    <ListItemIcon className={styles.drawerIcon}>
                      <SidebarIcons color={sectionColor} width={20} height={20} />
                    </ListItemIcon>

                    <ListItemText
                      className={
                        isSectionActive
                          ? `${styles.drawerText} ${styles.drawerSectionActiveText}`
                          : styles.drawerText
                      }
                      disableTypography
                      sx={{
                        flex: 1,
                        color: `${sectionColor} !important`,
                      }}
                    >
                      {t(`SIDEBAR.${item.name}`)}
                    </ListItemText>

                    <Box
                      className={`${styles.nestedItemChevron} ${
                        isSystemTablesExpanded
                          ? styles.nestedItemChevronExpanded
                          : ""
                      }`}
                      sx={{ color: sectionColor }}
                    >
                      <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                    </Box>
                  </Box>
                </ListItemButton>
              </Box>

              {isSystemTablesExpanded &&
                systemTablesSubItems.map((subItem) => {
                  const isSubActive = isSubItemActive(subItem.route);
                  const subItemClass = isSubActive
                    ? `${styles.drawerItem} ${styles.subDrawerItem} ${styles.drawerListItems}`
                    : `${styles.drawerItem} ${styles.subDrawerItem}`;
                  const subTextClass = isSubActive
                    ? `${styles.drawerText} ${styles.drawerListItemText} ${styles.drawerSectionActiveText}`
                    : styles.drawerText;
                  const subIconColor = isSubActive
                    ? theme.palette.primary.main
                    : theme.palette.text.primary;

                  return (
                    <React.Fragment key={subItem.route}>
                      {subItem.showDividerBefore && (
                        <Divider className={styles.subDrawerDivider} />
                      )}

                      <Link
                        to={`/system-tables/${subItem.route}`}
                        className={styles.drawerLink}
                        onClick={() =>
                          setIsSystemManagementClosedManually(false)
                        }
                      >
                        <ListItemButton className={subItemClass}>
                          <Box className={styles.subDrawerItemContent}>
                            <ListItemIcon
                              className={`${styles.drawerIcon} ${styles.subDrawerIcon}`}
                            >
                              <SidebarIcons
                                color={subIconColor}
                                width={20}
                                height={20}
                              />
                            </ListItemIcon>

                            <ListItemText
                              className={subTextClass}
                              disableTypography
                              sx={{ flex: 1 }}
                            >
                              {t(`SIDEBAR.${subItem.name}`)}
                            </ListItemText>
                          </Box>
                        </ListItemButton>
                      </Link>

                      {subItem.showDividerAfter && (
                        <Divider className={styles.subDrawerDivider} />
                      )}
                    </React.Fragment>
                  );
                })}
            </React.Fragment>
          );
        }

        const button = (
          <ListItemButton
            className={itemClass}
            {...(isSystemMgmt && { onClick: openSystemManagement })}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <ListItemIcon className={styles.drawerIcon}>
                {item.IconComponent
                  ? <item.IconComponent color={iconColor} width={20} height={20} />
                  : <SidebarIcons color={iconColor} width={20} height={20} />}
              </ListItemIcon>

              <ListItemText
                className={textClass}
                disableTypography
                sx={{ flex: 1 }}
              >
                {t(`SIDEBAR.${item.name}`)}
              </ListItemText>
            </Box>
          </ListItemButton>
        );

        if (isSystemMgmt) {
          return (
            <Box key={item.route} className={styles.drawerLink}>
              {button}
            </Box>
          );
        }

        return (
          <Link
            key={item.route}
            to={item.to!}
            className={styles.drawerLink}
            {...(isSystemManagementOpen && {
              onClick: () => setIsSystemManagementClosedManually(false),
            })}
          >
            {button}
          </Link>
        );
      })}
    </>
  );
};
